import React from 'react';
import { ArrowRight, Building2, Warehouse, Zap, Wrench } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-300/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          {/* Main Heading */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
            Building Into The
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-400"> Future</span>
            </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
            Your trusted partner for construction, prefab steel buildings, generators, and quality tools in Jamaica.
          </p>

          {/* Eye-Catching Sales Rep CTA Box */}
          <div className="mb-8 relative">
            <div className="inline-block relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 rounded-lg blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
              <a href="/sales-rep" className="relative block">
                <div className="relative bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-lg shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105">
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-3xl animate-bounce">💼</span>
                    <div className="text-left">
                      <div className="text-xl font-bold">Join Our Sales Team!</div>
                      <div className="text-sm text-blue-100">💰 Earn UP TO USD $500 Per Confirmed Generator Sale + Car Upkeep</div>
                    </div>
                    <span className="text-2xl ml-2">→</span>
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Get A Quote
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button
              onClick={() => scrollToSection('services')}
              size="lg"
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg transition-all duration-300"
            >
              Our Services
            </Button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <button
              onClick={() => scrollToSection('construction-services')}
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 cursor-pointer"
            >
              <Building2 className="w-10 h-10 text-blue-600 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-gray-900 mb-1">Construction</h3>
              <p className="text-sm text-gray-600">Expert Services</p>
            </button>
            <button
              onClick={() => scrollToSection('prefab-steel-buildings')}
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 cursor-pointer"
            >
              <Warehouse className="w-10 h-10 text-blue-600 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-gray-900 mb-1">Prefab Steel</h3>
              <p className="text-sm text-gray-600">Custom Buildings</p>
            </button>
            <button
              onClick={() => scrollToSection('generator-solutions')}
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 cursor-pointer"
            >
              <Zap className="w-10 h-10 text-blue-600 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-gray-900 mb-1">Generators</h3>
              <p className="text-sm text-gray-600">All Sizes</p>
            </button>
            <button
              onClick={() => scrollToSection('tools-and-equipment')}
              className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 cursor-pointer"
            >
              <Wrench className="w-10 h-10 text-blue-600 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-gray-900 mb-1">Tools</h3>
              <p className="text-sm text-gray-600">Quality Equipment</p>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;